import { useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import type { Address } from "viem";
import { launchRpcUrl } from "../../../contracts/launchpad";
import { launchChain } from "../../../api/launchpad";
import { useLiveRuntime } from "../../../state/live-runtime";
import { WalletOperation } from "../../../wallet/wallet-operation";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import { launchTx, subscribeLaunchTx } from "../../../wallet/launchpad-transactions";

export function useLaunchWallet(context: string) {
  const { walletController: wallet } = useLiveRuntime();
  const snapshot = useSyncExternalStore(wallet.subscribe, wallet.getSnapshot);
  const identity = wallet.getIdentityRevision();
  const scope = useRef({ key: "", generation: 0 });
  const [busy, setBusy] = useState(false);
  const active = useRef(false);
  const key = JSON.stringify([context, identity, snapshot.account]);
  useLayoutEffect(() => {
    scope.current = { key, generation: scope.current.generation + 1 };
    return () => { scope.current = { key: "", generation: scope.current.generation + 1 }; };
  }, [key]);
  async function run<T>(action: (account: Address, provider: Eip1193Provider, check: () => void) => Promise<T>,
    switchNetwork = false) {
    const provider = wallet.getProvider();
    const account = wallet.getAccount();
    if (!provider || !account || import.meta.env.VITE_APP_MODE === "DEMO") throw new Error("Connect your wallet first.");
    if (active.current) throw new Error("A wallet operation is already in progress.");
    if (scope.current.key !== key || wallet.getIdentityRevision() !== identity || account !== snapshot.account ||
        wallet.getSnapshot().chainId !== snapshot.chainId) throw new Error("Wallet context changed.");
    const readScope = () => String(scope.current.generation);
    const guard = new WalletOperation(wallet, provider, readScope, readScope());
    active.current = true;
    setBusy(true);
    try {
      if (switchNetwork) return await guard.switchTo(launchChain, () => new URL(launchRpcUrl));
      if (snapshot.chainId !== launchChain) throw new Error("Switch to Elysium to continue.");
      return await action(account, provider, guard.assertCurrent);
    } finally {
      guard.dispose();
      active.current = false;
      setBusy(false);
    }
  }
  return { snapshot, identity, revision: snapshot.sessionRevision, busy, run, switchNetwork: () => run(async () => {}, true) };
}
export const transactionScope = (account: string | null, operation: string) =>
  launchChain + ":" + (account?.toLowerCase() ?? "") + ":" + operation;
export function useLaunchTransaction(scope: string) {
  return useSyncExternalStore(subscribeLaunchTx, () => launchTx(scope));
}
